import React, {
	type SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from 'react';

import Fuse from 'fuse.js';
import debounce from 'lodash/debounce';

import Badge from '@atlaskit/badge/badge';
import Button from '@atlaskit/button/default/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItemCheckbox from '@atlaskit/dropdown-menu/dropdown-item-checkbox';
import DropdownItemCheckboxGroup from '@atlaskit/dropdown-menu/dropdown-item-checkbox-group';
import FilterIcon from '@atlaskit/icon/core/filter';
import SearchIcon from '@atlaskit/icon/core/search';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { Show, UNSAFE_useMediaQuery } from '@atlaskit/primitives/responsive';
import Spinner from '@atlaskit/spinner/spinner';
import Textfield from '@atlaskit/textfield/text-field';

import { getCoreIcons } from '../get-core-icons';
import { getIconLabCoreIcons } from '../get-icon-lab-core-icons';
import type { NewCoreIconData } from '../types';

import GridSection from './grid-section';
import { IconDetails } from './icon-details';
import NoResults from './no-results';

const globalCoreIconImportPrefix = '@atlaskit/icon/core';
const iconLabCoreIconImportPrefix = '@atlaskit/icon-lab/core';

// Extract the status type from NewIconBaseProps
type IconStatus = NonNullable<NewCoreIconData['status']>;

// @ts-ignore
const fuseOptions: Fuse.IFuseOptions<NewCoreIconData> = {
	keys: [
		{
			name: 'componentName',
			weight: 2,
		},
		{
			name: 'iconName',
			weight: 2,
		},
		{
			name: 'keywords',
			weight: 1,
		},
	],
	threshold: 0.4,
	ignoreLocation: true,
};

const IconDetailsBoxStyles = xcss({
	position: 'sticky',
	insetBlockStart: 'space.100',
});

const modalIconDetailsStyles = xcss({
	width: '100%',
	borderRadius: 'radius.small',
	overflow: 'auto',
});

type IconExplorerProps = {
	testId?: string;
};

const SearchIconBefore = () => (
	<Box paddingInlineStart="space.100">
		<SearchIcon label="Search" color="currentColor" />
	</Box>
);

// Filters state
const filtersInitialState = {
	status: {
		published: true,
		deprecated: false,
	} as Record<IconStatus, boolean>,
};

type FilterState = typeof filtersInitialState;

type FilterAction = {
	type: 'status';
	payload: Partial<Record<IconStatus, boolean>>;
};

const filterReducer = (state: FilterState, action: FilterAction) => ({
	[action.type]: {
		...state[action.type],
		...action.payload,
	},
});

const filterItemStyles = xcss({
	flex: '0 0 auto',
});

const FilterItem = ({ children }: { children: React.ReactNode }) => (
	<Box xcss={filterItemStyles}>{children}</Box>
);

export function IconExplorer({ testId }: IconExplorerProps): React.JSX.Element {
	const [selectedIcon, setSelectedIcon] = useState<NewCoreIconData>();
	const coreFuseIndex = useRef<Fuse<NewCoreIconData> | null>(null);
	const iconLabFuseIndex = useRef<Fuse<NewCoreIconData> | null>(null);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [searchedIcons, setSearchedIcons] = useState<{
		core: NewCoreIconData[] | undefined;
		iconLab: NewCoreIconData[] | undefined;
	}>();
	const [allIcons, setAllIcons] = useState<{
		core: NewCoreIconData[] | undefined;
		iconLab: NewCoreIconData[] | undefined;
	}>();

	/**
	 * Filters
	 */
	const [filterState, dispatchFilter] = useReducer(filterReducer, filtersInitialState);

	const filterIconsByStatus = useCallback(
		(
			icons:
				| {
						core: NewCoreIconData[] | undefined;
						iconLab: NewCoreIconData[] | undefined;
				  }
				| undefined,
			statusFilter: Record<IconStatus, boolean>,
		) => {
			if (!icons) {
				return icons;
			}

			const filterByStatus = <T extends NewCoreIconData>(
				iconList: T[] | undefined,
			): T[] | undefined => {
				if (!iconList) {
					return iconList;
				}
				return iconList.filter((icon) => {
					const iconStatus = icon.status;
					if (!iconStatus) {
						return false;
					}
					return statusFilter[iconStatus as IconStatus];
				});
			};

			return {
				core: filterByStatus(icons.core),
				iconLab: filterByStatus(icons.iconLab),
			};
		},
		[],
	);

	const search = (query: string) => {
		return setSearchedIcons({
			core: coreFuseIndex.current
				? coreFuseIndex.current.search<NewCoreIconData>(query).map((result) => result.item)
				: undefined,
			iconLab: iconLabFuseIndex.current
				? iconLabFuseIndex.current.search<NewCoreIconData>(query).map((result) => result.item)
				: undefined,
		});
	};

	const debouncedSearch = useMemo(() => debounce(search, 300), []);

	const handleSearch = useCallback(
		(query: string) => {
			setSearchQuery(query);

			if (query === '') {
				// Apply current filters to all icons
				const filteredIcons = filterIconsByStatus(allIcons, filterState.status);
				setSearchedIcons(filteredIcons);
				return;
			}

			// Clear searched tokens to enter loading state
			setSearchedIcons(undefined);

			if (query !== '') {
				debouncedSearch(query);
			}
		},
		[debouncedSearch, allIcons, filterIconsByStatus, filterState.status],
	);

	// Re-indexes search with filtered data
	const setFuseIndex = useCallback(
		(statusFilter: Record<IconStatus, boolean>) => {
			if (!allIcons) {
				return;
			}

			const filteredIcons = filterIconsByStatus(allIcons, statusFilter);

			if (filteredIcons?.core) {
				coreFuseIndex.current = new Fuse(filteredIcons.core, fuseOptions);
			}
			if (filteredIcons?.iconLab) {
				iconLabFuseIndex.current = new Fuse(filteredIcons.iconLab, fuseOptions);
			}

			// Update search if active
			if (searchQuery !== '') {
				// Re-run the search with current query
				handleSearch(searchQuery);
			} else {
				// If no search query, show filtered icons
				setSearchedIcons(filteredIcons);
			}
		},
		[allIcons, filterIconsByStatus, searchQuery, handleSearch],
	);

	const handleFilterChange = (action: FilterAction) => {
		dispatchFilter(action);

		const newFilterState = {
			...filterState.status,
			...action.payload,
		} as Record<IconStatus, boolean>;

		setFuseIndex(newFilterState);
	};

	const isBelowSm = UNSAFE_useMediaQuery('below.sm');

	useEffect(() => {
		const coreIconsPromise = getCoreIcons();
		const iconLabCoreIconsPromise = getIconLabCoreIcons();

		Promise.all([coreIconsPromise, iconLabCoreIconsPromise]).then(([coreIcons, iconLabIcons]) => {
			const allIcons = {
				core: coreIcons ? Object.values(coreIcons) : undefined,
				iconLab: iconLabIcons ? Object.values(iconLabIcons) : undefined,
			};
			setAllIcons(allIcons);
			const filteredIcons = filterIconsByStatus(allIcons, filtersInitialState.status);
			if (filteredIcons?.core) {
				coreFuseIndex.current = new Fuse(filteredIcons.core, fuseOptions);
			}
			if (filteredIcons?.iconLab) {
				iconLabFuseIndex.current = new Fuse(filteredIcons.iconLab, fuseOptions);
			}
			setSearchedIcons(filteredIcons);
		});
	}, [filterIconsByStatus]);

	const closeModal = () => {
		setIsModalOpen(false);
		setSelectedIcon(undefined);
	};

	const updateSelectedIcon = useCallback(
		(icon: NewCoreIconData) => {
			setSelectedIcon((oldIcon) => (oldIcon?.packageName !== icon.packageName ? icon : undefined));
			isBelowSm?.matches && setIsModalOpen(true);
		},
		[setSelectedIcon, setIsModalOpen, isBelowSm?.matches],
	);

	return (
		<Inline space="space.200" testId={testId}>
			<Stack space="space.400" grow="fill">
				<Stack space="space.200">
					<Textfield
						placeholder="Search"
						elemBeforeInput={<SearchIconBefore />}
						onChange={(evt: SyntheticEvent<HTMLInputElement>) =>
							handleSearch(evt.currentTarget.value)
						}
					/>
					<Inline space="space.200" alignBlock="center" shouldWrap={true}>
						<FilterItem>
							<DropdownMenu<HTMLButtonElement>
								shouldRenderToParent
								testId={testId && `${testId}-filters`}
								trigger={({ triggerRef, ...props }) => (
									<Button {...props} ref={triggerRef} iconAfter={FilterIcon}>
										Filters{' '}
										<Badge appearance={props.isSelected ? 'information' : undefined}>
											{Object.values(filterState.status).filter((v) => v).length}
										</Badge>
									</Button>
								)}
							>
								<DropdownItemCheckboxGroup id="status" title="Status">
									<DropdownItemCheckbox
										testId={testId && `${testId}-filters-published`}
										id="published"
										isSelected={filterState.status.published}
										onClick={() =>
											handleFilterChange({
												type: 'status',
												payload: {
													published: !filterState.status.published,
												},
											})
										}
									>
										Published
									</DropdownItemCheckbox>
									<DropdownItemCheckbox
										testId={testId && `${testId}-filters-deprecated`}
										id="deprecated"
										isSelected={filterState.status.deprecated}
										onClick={() =>
											handleFilterChange({
												type: 'status',
												payload: {
													deprecated: !filterState.status.deprecated,
												},
											})
										}
									>
										Deprecated
									</DropdownItemCheckbox>
								</DropdownItemCheckboxGroup>
							</DropdownMenu>
						</FilterItem>
					</Inline>
				</Stack>

				{searchedIcons?.core && searchedIcons.core.length > 0 && (
					<GridSection
						testId={testId && `${testId}-core`}
						isLegacy={false}
						importPath={globalCoreIconImportPrefix}
						heading="Core"
						icons={searchedIcons?.core}
						onIconClick={updateSelectedIcon}
						selectedIcon={!isModalOpen ? selectedIcon?.packageName : undefined}
					/>
				)}
				{searchedIcons?.core === undefined && (
					<Spinner interactionName="loading core icons" label="Loading" />
				)}

				{searchedIcons?.iconLab && searchedIcons.iconLab.length > 0 && (
					<GridSection
						testId={testId && `${testId}-icon-lab`}
						isLegacy={false}
						importPath={iconLabCoreIconImportPrefix}
						heading="Icon Lab"
						icons={searchedIcons?.iconLab}
						onIconClick={updateSelectedIcon}
						selectedIcon={!isModalOpen ? selectedIcon?.packageName : undefined}
					/>
				)}

				{searchedIcons?.core &&
					searchedIcons.core.length === 0 &&
					searchedIcons?.iconLab &&
					searchedIcons.iconLab.length === 0 && <NoResults />}
				{searchedIcons?.iconLab === undefined && (
					<Spinner interactionName="loading icon lab icons" label="Loading" />
				)}
			</Stack>
			<ModalTransition>
				{isModalOpen &&
					selectedIcon && (
						// Close button is contained within the IconDetails
						// eslint-disable-next-line @atlaskit/design-system/use-modal-dialog-close-button
						<Modal onClose={closeModal} shouldScrollInViewport>
							<Box xcss={modalIconDetailsStyles}>
								<IconDetails icon={selectedIcon} isModal onClose={closeModal} />
							</Box>
						</Modal>
					)}
			</ModalTransition>
			<Show above="sm" xcss={IconDetailsBoxStyles}>
				<IconDetails icon={selectedIcon} />
			</Show>
		</Inline>
	);
}
